import Auth from "shared/@auth";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useState } from "react";

export function useSettingsQueries(year, options = { enableExtras: false }) {
  const { makeAuthedRequest } = Auth.useContainer();
  const [data, setData] = useState(null);

  // settings get
  const getSettings = async (selectedYear) => {
    return await makeAuthedRequest(
      `/management-team/settings/year/${selectedYear}`,
      {
        method: "GET",
      }
    ).then(async (res) => {
      if (res.ok) {
        let json = await res.json();
        setData(json);
        return json;
      } else {
        const body = await res.json();
        throw new Error(body.message);
      }
    });
  };

  // const { data, status, refetch } = useQuery(
  //   ["settings", year],
  //   async ({selectedYear = null}) =>
  //     await makeAuthedRequest(`/management-team/settings/${selectedYear || year}`, {
  //       method: "GET",
  //     }).then(
  //       async (res) => {
  //         if (res.ok) {
  //           return res.json();
  //         } else {
  //           const body = await res.json();
  //           throw new Error(body.message);
  //         }
  //       },
  //       {
  //         slateTime: 0,
  //         cacheTime: 2000
  //       }
  //     )
  // );

  // pdf get (API method not yet finished)
  const [pdfStatus, setPdfStatus] = useState("loading");
  const settingsPDFGet = useQuery(
    "settingsPdf",
    () =>
      makeAuthedRequest(`/management-team/settings/pdf`, {
        method: "GET",
      }).then(async (res) => {
        if (res.ok) {
          setPdfStatus("success");
        } else {
          setPdfStatus("error");
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    { enabled: !!options.enableExtras }
  );

  // add user
  // Keep mutations in a separate hook (see useSettingsMutations)

  // Note: mutations (POST/PUT) were moved to useSettingsMutations.
  // This hook is intended to provide only read/get behavior by default.

  // destructure manager data
  const {
    data: managerData,
    status: managerStatus,
    refetch: managerRefetch,
  } = useQuery(
    ["managers"],
    () =>
      makeAuthedRequest(`/management-team/managers`, { method: "GET" }).then(
        async (res) => {
          if (res.ok) {
            return res.json();
          } else {
            const body = await res.json();
            throw new Error(body.message);
          }
        }
      ),
    { enabled: !!options.enableExtras }
  );

  // add manager post
  // manager mutations live in useSettingsMutations
  // manager mutations live in useSettingsMutations

  // Notifications
  const {
    data: settingsNotificationData,
    status: settingsNotificationStatus,
    refetch: settingsNotificationRefetch,
  } = useQuery(
    "settings-notifications-get",
    () =>
      makeAuthedRequest(`/management-team/settings/notifications`, {
        method: "GET",
      }).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    { enabled: !!options.enableExtras }
  );

  // add admin
  return {
    data,
    settingsPDFGet,
    pdfStatus,
    managerData,
    managerStatus,
    settingsNotificationData,
    settingsNotificationStatus,
    settingsNotificationRefetch,
    getSettings,
  };
}

export function useSettingsMutations(year) {
  const { makeAuthedRequest } = Auth.useContainer();
  const queryClient = useQueryClient();

  const settingsAddUser = useMutation(
    async ({ user, accessLevels, iboNumber }) =>
      makeAuthedRequest(`/management-team/settings/add/user`, {
        method: "POST",
        body: JSON.stringify({ user, accessLevels, iboNumber }),
      }).then(async (res) => {
        if (res.ok) {
          return res;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      })
  );

  const settingsYearPost = useMutation(async (_year) =>
    makeAuthedRequest(`/management-team/settings/add/year`, {
      method: "POST",
      body: JSON.stringify(_year),
    }).then(async (res) => {
      if (res.ok) {
        return res;
      } else {
        const body = await res.json();
        throw new Error(body.message);
      }
    })
  );

  const settingsYearActivatePost = useMutation(
    async (_year) =>
      makeAuthedRequest(`/management-team/settings/activate/${_year}`, {
        method: "POST",
      }).then(async (res) => {
        if (res.ok) {
          return res;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries("dashboard");
      },
    }
  );

  const settingsYearCalculatePost = useMutation(
    async (_year) =>
      makeAuthedRequest(`/management-team/settings/calculate/${_year}`, {
        method: "POST",
      }).then(async (res) => {
        if (res.ok) {
          return res;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries("dashboard");
      },
    }
  );

  const settingsYearImportDiamondsPost = useMutation(
    async (_year) =>
      makeAuthedRequest(`/management-team/diamond-sync/${_year}`, {
        method: "POST",
      }).then(async (res) => {
        if (res.ok) {
          return res;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries("dashboard");
      },
    }
  );

  const settingsPut = useMutation("settings", async (settingsArray) =>
    makeAuthedRequest(`/management-team/settings/edit/${year}`, {
      method: "PUT",
      body: JSON.stringify(settingsArray),
    }).then(async (res) => {
      if (res.ok) {
        return;
      } else {
        const body = await res.json();
        throw new Error(body.message);
      }
    })
  );

  // manager mutations
  const managerAddPost = useMutation(
    "managerAdd",
    async (newManagerObject) =>
      makeAuthedRequest(`/management-team/manager/add`, {
        method: "POST",
        body: JSON.stringify(newManagerObject),
      }).then(async (res) => {
        if (res.ok) {
          return res;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => queryClient.invalidateQueries(["managers"]),
    }
  );

  const managerEditPut = useMutation(
    "managerEdit",
    async (changedManagersObject) =>
      makeAuthedRequest(`/management-team/manager/edit`, {
        method: "PUT",
        body: JSON.stringify(changedManagersObject),
      }).then(async (res) => {
        if (res.ok) {
          return res;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => queryClient.invalidateQueries(["managers"]),
    }
  );

  const managerDelete = useMutation(
    "managerDelete",
    async (managerDeleteObject) =>
      makeAuthedRequest(`/management-team/manager/delete`, {
        method: "DELETE",
        body: JSON.stringify(managerDeleteObject),
      }).then(async (res) => {
        if (res.ok) {
          return res;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => queryClient.invalidateQueries(["managers"]),
    }
  );

  const settingsAddAdminsToNotificationsPost = useMutation(
    "adminAdd",
    async (admin) =>
      makeAuthedRequest(`/management-team/settings/notifications`, {
        method: "POST",
        body: JSON.stringify(admin),
      }).then(async (res) => {
        if (res.ok) {
          return res;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () =>
        queryClient.invalidateQueries(["settings-notifications-get"]),
    }
  );

  const adminDelete = useMutation(
    "adminDelete",
    async (adminToDelete) =>
      makeAuthedRequest(`/management-team/settings/notifications`, {
        method: "DELETE",
        body: JSON.stringify(adminToDelete),
      }).then(async (res) => {
        if (res.ok) {
          return res;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () =>
        queryClient.invalidateQueries(["settings-notifications-get"]),
    }
  );

  return {
    settingsAddUser,
    settingsYearPost,
    settingsYearActivatePost,
    settingsYearCalculatePost,
    settingsYearImportDiamondsPost,
    settingsPut,
    managerAddPost,
    managerEditPut,
    managerDelete,
    settingsAddAdminsToNotificationsPost,
    adminDelete,
  };
}

// Combined hook for backward compatibility
export function useSettings(year, options = { enableExtras: false }) {
  const queries = useSettingsQueries(year, options);
  const mutations = useSettingsMutations(year);

  // Wrap some mutations so they trigger getSettings when appropriate (preserve previous behavior)
  const wrapWithRefresh = (
    mutation,
    shouldRefresh = false,
    alwaysUseYear = false
  ) => {
    if (!mutation) return mutation;
    return {
      ...mutation,
      mutate: (arg, opts = {}) => {
        const originalOnSuccess = opts.onSuccess;
        mutation.mutate(arg, {
          ...opts,
          onSuccess: async (...args) => {
            if (shouldRefresh) {
              if (alwaysUseYear) {
                await queries.getSettings(year);
              } else {
                await queries.getSettings(arg || year);
              }
            }
            if (originalOnSuccess) {
              originalOnSuccess(...args);
            }
          },
        });
      },
      mutateAsync: async (arg, opts) => {
        const res = await mutation.mutateAsync(arg, opts);
        if (shouldRefresh) {
          if (alwaysUseYear) {
            await queries.getSettings(year);
          } else {
            await queries.getSettings(arg || year);
          }
        }
        return res;
      },
    };
  };

  return {
    data: queries.data,
    settingsPDFGet: queries.settingsPDFGet,
    pdfStatus: queries.pdfStatus,
    settingsYearPost: mutations.settingsYearPost,
    settingsPut: wrapWithRefresh(mutations.settingsPut, true, true),
    managerData: queries.managerData,
    managerStatus: queries.managerStatus,
    managerAddPost: wrapWithRefresh(mutations.managerAddPost, true),
    managerEditPut: wrapWithRefresh(mutations.managerEditPut, true),
    managerDelete: wrapWithRefresh(mutations.managerDelete, true),
    getSettings: queries.getSettings,
    settingsYearActivatePost: wrapWithRefresh(
      mutations.settingsYearActivatePost,
      true
    ),
    settingsYearCalculatePost: wrapWithRefresh(
      mutations.settingsYearCalculatePost,
      true
    ),
    settingsYearImportDiamondsPost: wrapWithRefresh(
      mutations.settingsYearImportDiamondsPost,
      true
    ),
    settingsAddAdminsToNotificationsPost:
      mutations.settingsAddAdminsToNotificationsPost,
    settingsNotificationData: queries.settingsNotificationData,
    settingsNotificationStatus: queries.settingsNotificationStatus,
    settingsNotificationRefetch: queries.settingsNotificationRefetch,
    adminDelete: mutations.adminDelete,
    settingsAddUser: mutations.settingsAddUser,
  };
}
